import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { agencies } from '../mockData';
import AnimatedSection from '../components/AnimatedSection';

const AgencesPage = () => {
  const agencyImages = [
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
    'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800'
  ];

  const agenciesWithImages = agencies.map((agency, index) => ({
    ...agency,
    image: agencyImages[index % agencyImages.length]
  }));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div
        className="relative h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Nos agences</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">Notre atelier en Alpes-de-Haute-Provence</h1>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Rencontrez MRTP SARL</h2>
        </div>
      </div>

      {/* Agencies List */}
      <div className="pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {agenciesWithImages.map((agency, index) => (
              <AnimatedSection 
                key={agency.id} 
                animation={index % 2 === 0 ? 'slide-left' : 'slide-right'}
                delay={index * 150}
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="relative h-80 rounded-lg overflow-hidden shadow-lg group">
                      <img
                        src={agency.image}
                        alt={agency.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>
                  
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">{agency.name}</h2>
                    
                    <div className="space-y-4 mb-6">
                      <div>
                        <p className="text-gray-700 text-lg">{agency.address}</p>
                        <p className="text-gray-700 text-lg">{agency.postalCode} {agency.city}</p>
                      </div>
                    </div>

                    <div className="flex space-x-4 mb-6">
                      <a
                        href={agency.mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-gray-100 hover:bg-[#c4d600] rounded-full flex items-center justify-center transition-all duration-300 group hover:scale-110"
                        aria-label="Voir sur la carte"
                      >
                        <MapPin className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                      </a>
                      <a
                        href={`tel:${agency.phone.replace(/\\s/g, '')}`}
                        className="w-12 h-12 bg-gray-100 hover:bg-[#c4d600] rounded-full flex items-center justify-center transition-all duration-300 group hover:scale-110"
                        aria-label="Appeler"
                      >
                        <Phone className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                      </a>
                      <a
                        href={`mailto:${agency.email}`}
                        className="w-12 h-12 bg-gray-100 hover:bg-[#c4d600] rounded-full flex items-center justify-center transition-all duration-300 group hover:scale-110"
                        aria-label="Envoyer un email"
                      >
                        <Mail className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                      </a>
                    </div>

                    <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold transition-all duration-200 hover:scale-105">
                      <Link to={`/agence/${agency.id}`}>voir l'agence</Link>
                    </Button>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">VENEZ NOUS RENCONTRER</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Un atelier à Villars-Colmars</h2>
            <div className="flex justify-center mb-8">
              <img
                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600"
                alt="Carte Alpes-de-Haute-Provence"
                className="w-full max-w-md rounded-lg shadow-md"
              />
            </div>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Vous souhaitez faire construire votre chalet ou votre maison secondaire dans les Alpes-de-Haute-Provence ? Venez à la rencontre de notre équipe dans notre atelier de Villars-Colmars. Nous intervenons dans les vallées de l'Ubaye, du Haut-Verdon et de l'ensemble des Alpes du Sud.
            </p>
          </div>
        </div>
      </div>

      {/* About Region Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Construire dans les Alpes du Sud : rencontrons-nous !</h2>
          <p className="text-lg text-gray-600 mb-4">
            C'est décidé, vous allez faire construire votre maison dans les Alpes-de-Haute-Provence ? Félicitations ! C'est un projet ambitieux et merveilleux. Pour vous conseiller au mieux et vous accompagner tout au long de ce projet, rencontrez notre équipe locale à Villars-Colmars.
          </p>
          <p className="text-lg text-gray-600 mb-6">
            Découvrez <Link to="/modeles" className="text-[#c4d600] hover:underline">nos modèles de maison</Link> et prenez l'inspiration dont vous avez besoin pour concevoir votre projet personnalisé ! Nous nous adaptons à toutes vos contraintes pour dessiner et construire le projet qu'il vous faut.
          </p>

          <h3 className="text-2xl font-bold text-gray-800 mb-4 mt-8">Faire construire en montagne</h3>
          <p className="text-lg text-gray-600 mb-4">
            Le territoire alpin regorge de trésors ! Entre les sommets, les forêts de mélèzes et les rivières d'altitude, chaque terrain nécessite une approche sur-mesure.
          </p>
          <p className="text-lg text-gray-600 mb-4">
            Si vous êtes amoureux de la nature, vous serez comblé. À quelques kilomètres de votre maison, partez explorer le Parc national du Mercantour, les cirques rocheux et les nombreux sentiers de randonnée.
          </p>
          <p className="text-lg text-gray-600">
            Pour les gourmands, le terroir provençal offre une cuisine généreuse. Imaginez un apéritif face aux montagnes, sur la terrasse de votre nouvelle réalisation MRTP SARL.
          </p>
        </div>
      </div>

      {/* Personalization Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Faire construire VOTRE maison 100 % personnalisée</h2>
          <p className="text-lg text-gray-600 mb-4">
            Choisir nos équipes, c'est faire le choix d'une construction à votre image. Résultat ? Plus d'espace, davantage d'indépendance et une pleine liberté.
          </p>
          <p className="text-lg text-gray-600 mb-4">
            Vous avez du mal à vous projeter ? Nos architectes ont déjà conçu 75 modèles sur lesquels vous pouvez vous baser. Maisons contemporaines, traditionnelles ou ultra-modernes, il y en a pour tous les goûts.
          </p>
          <p className="text-lg text-gray-600">
            Notre raison d'être ? Vous conseiller au mieux et contribuer à l'épanouissement de toute votre famille. Nos conseillers en architecture s'adaptent à la complexité de votre terrain, vos goûts, vos besoins, vos habitudes de vie et votre budget.
          </p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Un projet ? Prenons un café ensemble !</h2>
              <p className="text-lg text-gray-600 mb-4">
                Avec MRTP SARL, plongez au cœur d'une aventure humaine ! Nos équipes se tiennent à votre disposition sur toute la durée de votre projet pour vous accompagner au mieux. Depuis 1985, nous œuvrons chaque jour pour vous offrir, dans les temps, la maison de vos rêves.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                <strong>Où nous trouver :</strong>
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-center text-gray-600">
                  <span className="w-2 h-2 bg-[#c4d600] rounded-full mr-3"></span>
                  5 Lot. Côte Meunière — 04370 Villars-Colmars
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="w-2 h-2 bg-[#c4d600] rounded-full mr-3"></span>
                  Interventions dans les vallées du Haut-Verdon et de l'Ubaye
                </li>
                <li className="flex items-center text-gray-600">
                  <span className="w-2 h-2 bg-[#c4d600] rounded-full mr-3"></span>
                  Rendez-vous possibles sur chantier ou en visio
                </li>
              </ul>
              <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold text-lg px-8 py-6">
                <Link to="/contact">Venez-nous rendre visite !</Link>
              </Button>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800"
                alt="Équipe MRTP SARL"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencesPage;